import React, { useState } from 'react';
import { formatMoney } from '../../lib/formatMoney';

/**
 * Choose a method first, then see that method's details (Option 1 layout).
 *
 * A single row of equal-sized, selectable tiles — Zelle, Card, Check (only the
 * available ones) — each showing the method name, the amount THAT method takes,
 * and a fee tag. Below sits one details panel for the currently selected method
 * with its existing action button. Selecting a tile only switches which details
 * show; it NEVER starts a payment. Only the action button in the details panel
 * calls onChoose(...), which routes through the existing confirm-method dialog.
 *
 * All amounts, fees, method values ('zelle' | 'card' | 'check'), handler calls,
 * busy states, blockedReason / early-pay handling, and copy behavior are
 * unchanged from before — only the layout/interaction changed.
 */
export const PayOptions = ({
  row,
  zelleHandle,
  cardTotal,
  fee,
  onChoose,
  onCopyZelle,
  processing,
  blockedReason = null,
  earlyPay = false,
  checkInfo = null,
  testPrefix = 'cv-pay',
}) => {
  const netAmount = Number(row?.amount) || 0;
  const busy = processing === row?.paymentId;
  const checkAvailable = !!(checkInfo?.enabled && (checkInfo?.payableTo || checkInfo?.address));

  // Default selection: Zelle if available, otherwise the first available method.
  // (Zelle is always offered here, same as before.)
  const [selected, setSelected] = useState('zelle');

  // A row the server would refuse says so, in words, and renders NO control.
  if (blockedReason) {
    return (
      <div
        style={{
          marginTop: 18, borderRadius: 12, padding: '14px 16px',
          background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(255,255,255,0.06)',
        }}
        data-testid={`${testPrefix}-blocked-${row?.paymentId}`}
      >
        <p style={{ margin: 0, fontSize: 13, color: '#8f9099' }}>{blockedReason}</p>
      </div>
    );
  }

  const goldBtn = {
    width: '100%', padding: 15, border: 'none', borderRadius: 11,
    cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.7 : 1,
    fontWeight: 600, fontSize: 15, color: '#1a1500',
    background: 'linear-gradient(180deg, #E8C979, #D4AF37)',
    boxShadow: '0 10px 26px -12px rgba(212,175,55,0.6)',
  };
  const outlineBtn = {
    width: '100%', padding: 15, borderRadius: 11,
    cursor: busy ? 'not-allowed' : 'pointer', opacity: busy ? 0.7 : 1,
    fontWeight: 600, fontSize: 15, color: '#EAEAEA',
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid rgba(255,255,255,0.14)',
  };
  // BLOCK S29b — nothing due yet: Zelle drops its gold fill for the outline so
  // early payment reads as optional rather than urgent (early-pay handling).
  const zelleBtnStyle = earlyPay ? outlineBtn : goldBtn;

  // The available methods, in order, with the exact numbers already computed.
  const methods = [
    { key: 'zelle', name: 'Zelle', amount: netAmount, chip: { text: 'No fee', tone: 'green' } },
    {
      key: 'card', name: 'Card', amount: cardTotal,
      chip: fee > 0 ? { text: `+${formatMoney(fee)} fee`, tone: 'muted' } : { text: 'No fee', tone: 'green' },
    },
    ...(checkAvailable ? [{ key: 'check', name: 'Check', amount: netAmount, chip: { text: 'No fee', tone: 'green' } }] : []),
  ];

  const sel = (selected === 'check' && !checkAvailable) ? 'zelle' : selected;

  const labelRow = (label, value, extra = null) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
      <span style={{ color: '#66676f' }}>{label}</span>
      <span style={{ color: '#EAEAEA', fontWeight: 600, textAlign: 'right', whiteSpace: 'pre-line' }}>
        {value}
        {extra}
      </span>
    </div>
  );

  const copyBtn = (text, tid) => (
    <button
      type="button"
      onClick={() => onCopyZelle(text)}
      style={{
        color: '#E8C979', fontSize: 12, textDecoration: 'underline',
        cursor: 'pointer', marginLeft: 8, background: 'transparent',
        border: 'none', padding: 0,
      }}
      data-testid={tid}
    >Copy</button>
  );

  return (
    <>
      <style>{`
        .po-tile { transition: border-color .18s ease, box-shadow .18s ease, background-color .18s ease; }
        .po-tile:focus-visible { outline: 2px solid #E8C979; outline-offset: 2px; }
        @media (prefers-reduced-motion: reduce){ .po-tile { transition: none; } }
        @media (max-width: 520px){ .po-tilerow { grid-template-columns: 1fr !important; } }
      `}</style>

      {earlyPay && (
        <p
          style={{ margin: '18px 0 0', fontSize: 13, color: '#8f9099', textAlign: 'center' }}
          data-testid={`${testPrefix}-early-caption-${row?.paymentId}`}
        >
          Nothing is due yet. Paying early is optional.
        </p>
      )}

      {/* ── 1. METHOD TILES — one row, equal sized, selectable ── */}
      <div
        className="po-tilerow"
        style={{
          marginTop: 18, display: 'grid', gap: 10,
          gridTemplateColumns: `repeat(${methods.length}, minmax(0, 1fr))`,
        }}
        data-testid={`${testPrefix}-options-${row?.paymentId}`}
      >
        {methods.map((m) => {
          const active = sel === m.key;
          return (
            <button
              key={m.key}
              type="button"
              className="po-tile"
              onClick={() => setSelected(m.key)}
              aria-pressed={active}
              style={{
                display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-start',
                textAlign: 'left', cursor: 'pointer', borderRadius: 12, padding: '12px 14px',
                background: active ? 'rgba(212,175,55,0.06)' : 'rgba(255,255,255,0.025)',
                border: active ? '1px solid rgba(212,175,55,0.55)' : '1px solid rgba(255,255,255,0.08)',
                boxShadow: active ? '0 8px 24px -14px rgba(212,175,55,0.65)' : 'none',
              }}
              data-testid={`${testPrefix}-method-${m.key}-${row?.paymentId}`}
            >
              <span style={{ fontSize: 13.5, fontWeight: 600, color: '#EAEAEA' }}>{m.name}</span>
              <span style={{ fontSize: 17, fontWeight: 700, color: '#EAEAEA', letterSpacing: '-0.3px' }}>
                {formatMoney(m.amount)}
              </span>
              <span
                style={{
                  fontSize: 10.5, fontWeight: 600, padding: '2px 8px', borderRadius: 99,
                  ...(m.chip.tone === 'green'
                    ? { color: '#8FBF9F', background: 'rgba(143,191,159,0.12)', border: '1px solid rgba(143,191,159,0.25)' }
                    : { color: '#8f9099', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.10)' }),
                }}
              >
                {m.chip.text}
              </span>
            </button>
          );
        })}
      </div>

      {/* ── 2. DETAILS for the selected method only (steady height) ── */}
      <div
        style={{
          marginTop: 12, borderRadius: 14, padding: '16px 16px 18px', minHeight: 168,
          background: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)',
          display: 'flex', flexDirection: 'column', gap: 12,
        }}
        data-testid={`${testPrefix}-details-${row?.paymentId}`}
      >
        {sel === 'zelle' && (
          <>
            <div
              style={{ fontSize: 13, display: 'flex', flexDirection: 'column', gap: 7 }}
              data-testid={`${testPrefix}-zelle-details-${row?.paymentId}`}
            >
              {labelRow(
                'Send Zelle to',
                zelleHandle || <span style={{ color: '#66676f', fontWeight: 400 }}>Ask us for details</span>,
                zelleHandle ? copyBtn(zelleHandle, `${testPrefix}-zelle-copy-${row?.paymentId}`) : null,
              )}
              {labelRow('Amount (exact)', formatMoney(netAmount))}
            </div>
            <button
              type="button"
              onClick={() => onChoose(row, 'zelle', netAmount)}
              disabled={busy}
              style={zelleBtnStyle}
              data-testid={`${testPrefix}-zelle-${row?.paymentId}`}
            >
              {busy ? 'Recording…' : `Pay by Zelle — ${formatMoney(netAmount)}`}
            </button>
          </>
        )}

        {sel === 'card' && (
          <>
            <div style={{ fontSize: 13, display: 'flex', flexDirection: 'column', gap: 7 }}>
              {labelRow('Amount', formatMoney(cardTotal))}
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                <span style={{ color: '#66676f' }}>
                  {fee > 0 ? 'Includes processing fee' : 'No processing fee'}
                </span>
                {fee > 0 && <span style={{ color: '#8f9099', fontWeight: 600 }}>{formatMoney(fee)}</span>}
              </div>
              <p style={{ margin: '2px 0 0', fontSize: 12.5, color: '#66676f' }}>
                Secure · Powered by Stripe
              </p>
            </div>
            <button
              type="button"
              onClick={() => onChoose(row, 'card', cardTotal)}
              disabled={busy}
              style={goldBtn}
              data-testid={`${testPrefix}-card-${row?.paymentId}`}
            >
              {busy ? 'Opening checkout…' : `Pay by card — ${formatMoney(cardTotal)}`}
            </button>
          </>
        )}

        {sel === 'check' && checkAvailable && (
          <>
            <div
              style={{ fontSize: 13, display: 'flex', flexDirection: 'column', gap: 7 }}
              data-testid={`${testPrefix}-check-details-${row?.paymentId}`}
            >
              {checkInfo?.payableTo && labelRow('Make payable to', checkInfo.payableTo)}
              {checkInfo?.address && labelRow(
                'Mail to',
                checkInfo.address,
                copyBtn(checkInfo.address, `${testPrefix}-check-copy-${row?.paymentId}`),
              )}
            </div>
            <button
              type="button"
              onClick={() => onChoose(row, 'check', netAmount)}
              disabled={busy}
              style={goldBtn}
              data-testid={`${testPrefix}-check-${row?.paymentId}`}
            >
              {busy ? 'Recording…' : "I've mailed a check"}
            </button>
          </>
        )}
      </div>
    </>
  );
};

export default PayOptions;
